'use client';

import type { ChangeEvent } from 'react';
import { Field, Button } from '@/shared/ui/atoms';
import type { FormProps } from './types';

export default function Form({
    fields,
    values,
    errors,
    onChange,
    onSubmit,
    honeypotName = 'website',
    status,
    errorMessage,
    submitLabel,
    sendingLabel = 'Sending…',
    isSubmitDisabled = false,
    submitEndIcon,
    classNames = {},
}: FormProps) {
    const isSending = status === 'sending';
    const label = isSending ? sendingLabel : submitLabel;

    return (
        <form onSubmit={onSubmit} className={classNames.form} noValidate>
            <input
                type="text"
                name={honeypotName}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className={classNames.honeypot}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
            />
            {fields.map((fieldConfig) => {
                const value = values[fieldConfig.name] ?? '';
                const isTextarea = fieldConfig.as === 'textarea';
                const fieldError = errors?.[fieldConfig.name];
                return (
                    <Field
                        key={fieldConfig.name}
                        as={isTextarea ? 'textarea' : 'input'}
                        name={fieldConfig.name}
                        type={fieldConfig.type ?? 'text'}
                        label={fieldConfig.label}
                        placeholder={fieldConfig.placeholder}
                        required={fieldConfig.required}
                        error={fieldError}
                        value={value}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(fieldConfig.name, e.target.value)}
                        disabled={isSending}
                        className={fieldError ? classNames.fieldRootError : undefined}
                        classNames={{
                            root: classNames.fieldRoot,
                            input: isTextarea ? classNames.textarea ?? classNames.input : classNames.input,
                            error: classNames.error,
                        }}
                    />
                );
            })}
            {status === 'error' && errorMessage && (
                <p className={classNames.error} role="alert">
                    {errorMessage}
                </p>
            )}
            <Button
                type="submit"
                className={classNames.submitBtn}
                disabled={isSubmitDisabled}
                endIcon={isSending ? undefined : submitEndIcon}
            >
                {label}
            </Button>
        </form>
    );
}
